import { useCallback, useEffect, useMemo, useState } from 'react'
import { get } from './lib/api'
import { VERSION, COMMIT, BUILD } from './lib/version'
import { refreshApp } from './lib/update'
import { me, signOut, type User } from './lib/auth'
import { cardFor, lightAt, sceneFor } from './lib/timeOfDay'
import { planRetired } from '../shared/show'
import SignIn from './components/SignIn'
import Account from './components/Account'
import Admin from './components/Admin'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'
import Icon from './components/Icon'
import ExportBackup from './components/ppc/ExportBackup'
import PlantList from './components/ppc/PlantList'
import PlantEditor from './components/ppc/PlantEditor'
import SpeciesList from './components/ppc/SpeciesList'
import PalmReference from './components/ppc/PalmReference'
import ShowScreen from './components/ppc/ShowScreen'
import PlanScreen from './components/ppc/PlanScreen'
import Maintenance from './components/ppc/Maintenance'

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  build: number
  commit: string
  setupPending?: boolean
}

type Tab = 'plants' | 'species' | 'show' | 'plan' | 'account' | 'admin' | 'maintenance'
/*
 * The palm reference is a SCREEN, not a tab.
 *
 * It is reached from the collection and from the species panel, because it is
 * a thing you consult while doing something else — naming a plant, deciding
 * what to charge for it. A tab of its own would spend one of four slots on the
 * bottom bar, on a phone, for a reference book.
 */
type Screen = { at: 'tabs' } | { at: 'plant'; id: string | null } | { at: 'palms' }

/*
 * Four tabs, species between the plants and the show.
 *
 * This used to be three on the argument that a species only matters in
 * relation to the plants pointing at it, so it belonged in the panel beside
 * the collection rather than in the bar. That held while species were
 * something you edited occasionally. With the palm reference feeding profiles
 * into the library it became somewhere Dillon goes on purpose, and a
 * destination you go to on purpose is a tab.
 *
 * Its position is the order of the work: catalogue the plant, name it, sell it.
 */
const ALL_TABS: [Tab, string, Parameters<typeof Icon>[0]['name']][] = [
  ['plants', 'My plants', 'leaf'],
  ['species', 'Species', 'book'],
  ['show', 'Show', 'bag'],
  ['plan', '15-day plan', 'calendar'],
]

/** Shown if someone lands on the retired plan by way of stale state. */
function PlantsFallback({ onGo }: { onGo: () => void }) {
  return (
    <div className="card empty">
      <h3>The show has been.</h3>
      <p>The fifteen-day plan has done its job. What was ticked off is still on record.</p>
      <button type="button" className="primary" onClick={onGo}>Back to the plants</button>
    </div>
  )
}

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [tab, setTab] = useState<Tab>('plants')
  const [screen, setScreen] = useState<Screen>({ at: 'tabs' })
  const [reloadKey, setReloadKey] = useState(0)
  /*
   * The masthead's admin controls, collapsed on a phone.
   *
   * Expanded, they wrap onto two rows and push "Add a plant" to 492px of a
   * 664px viewport — below the fold on a real handset once browser chrome is
   * counted. The button Dillon presses forty times a morning was off screen
   * while every automated check called it visible, because it was in the
   * layout. Being in the layout is not being on the screen.
   */
  const [menuOpen, setMenuOpen] = useState(false)

  const refreshUser = useCallback(async () => setUser(await me()), [])

  // Settled once per mount. The header should not change light under the user
  // mid-session just because a render happened to cross the hour.
  const light = useMemo(() => lightAt(), [])

  /*
   * The 15-day plan is a launch checklist, not a permanent feature. Three days
   * after the show it stops being offered — a countdown to a date in the past
   * is the clearest sign nobody maintains the thing. The rows stay in the
   * database; "did we test a real ATH payment?" is still answerable in November.
   */
  const TABS = useMemo(() => ALL_TABS.filter(([key]) => key !== 'plan' || !planRetired()), [])

  useEffect(() => {
    void (async () => {
      const [u, h] = await Promise.all([me(), get<Health>('/api/health').catch(() => null)])
      setUser(u)
      setHealth(h)
      setReady(true)
    })()
  }, [])

  if (!ready) return <div className="boot">Opening the nursery…</div>

  // `operations.write` is what staff holds: catalogue and sell. Every write
  // control asks this rather than a role, so adding a role cannot silently
  // grant or remove the ability to work.
  const canWrite = !!user?.capabilities.includes('operations.write')
  const go = (next: Tab) => { setTab(next); setScreen({ at: 'tabs' }) }

  return (
    <ErrorBoundary>
      <UpdateBar />
      <EnvBanner health={health} />

      <header className="masthead">
        <img className="masthead-mark" src={sceneFor(light)} alt="" width={48} height={48} />
        <span className="masthead-name">
          <b>Paradise Property Care</b>
          <span>Vieques, Puerto Rico</span>
        </span>
        {user && (
          <nav data-open={menuOpen}>
            {/* Phone only: everything below folds behind this. */}
            <button
              type="button" className="menu-toggle"
              aria-expanded={menuOpen} aria-label="Menu"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <Icon name={menuOpen ? 'close' : 'filter'} size={16} />
            </button>

            <span className="collapsible">
              {health?.environment !== 'production' && <span className="pill">Private working draft</span>}
              <ExportBackup />
              <button type="button" onClick={() => { go('account'); setMenuOpen(false) }} aria-current={tab === 'account'} title="Account">
                <Icon name="person" size={15} /><span className="sr-only">Account</span>
              </button>
              {user.capabilities.includes('admin.access') && (
                <>
                  <button type="button" onClick={() => { go('maintenance'); setMenuOpen(false) }} aria-current={tab === 'maintenance'} title="Maintenance">
                    <Icon name="wrench" size={15} /><span className="sr-only">Maintenance</span>
                  </button>
                  <button type="button" onClick={() => { go('admin'); setMenuOpen(false) }} aria-current={tab === 'admin'} title="Admin">
                    <Icon name="settings" size={15} /><span className="sr-only">Admin</span>
                  </button>
                </>
              )}
              <button type="button" onClick={async () => { await signOut(); await refreshUser() }}>Sign out</button>
            </span>
          </nav>
        )}
      </header>

      {user && (
        <>
          <nav className="tabrow">
            {TABS.map(([key, label, icon]) => (
              <button
                key={key} type="button"
                aria-current={tab === key && screen.at === 'tabs'}
                onClick={() => go(key)}
              >
                <Icon name={icon} size={15} /> {label}
              </button>
            ))}
            <span className="motto">Grow it. Know it. Sell it.</span>
          </nav>
          <div className="tabline"><hr /></div>
        </>
      )}

      <main>
        {!user && (
          <>
            <div className="signin-hero">
              <img src={cardFor(light)} alt="Paradise Property Care, Vieques, Puerto Rico" />
            </div>
            <SignIn onSignedIn={refreshUser} setupPending={!!health?.setupPending} />
          </>
        )}

        {user && screen.at === 'tabs' && (
          <>
            {tab === 'plants' && (
              <PlantList
                reloadKey={reloadKey}
                canWrite={canWrite}
                onAdd={() => setScreen({ at: 'plant', id: null })}
                onOpen={(plant) => setScreen({ at: 'plant', id: plant.id })}
                onSpecies={() => go('species')}
                onPalms={() => setScreen({ at: 'palms' })}
              />
            )}
            {tab === 'species' && (
              <SpeciesList
                canWrite={canWrite}
                onBack={() => go('plants')}
                onPalms={() => setScreen({ at: 'palms' })}
              />
            )}
            {tab === 'show' && <ShowScreen canWrite={canWrite} />}
            {/* Retired plans are unreachable from the tabs; this guards a stale
                tab selection surviving a refresh. */}
            {tab === 'plan' && (planRetired() ? <PlantsFallback onGo={() => go('plants')} /> : <PlanScreen canWrite={canWrite} />)}
            {tab === 'account' && <Account user={user} />}
            {tab === 'admin' && <Admin />}
            {tab === 'maintenance' && <Maintenance />}
          </>
        )}

        {user && screen.at === 'palms' && (
          <PalmReference canWrite={canWrite} onBack={() => setScreen({ at: 'tabs' })} />
        )}

        {user && screen.at === 'plant' && (
          <PlantEditor
            plantId={screen.id}
            onCancel={() => setScreen({ at: 'tabs' })}
            onDone={() => { setScreen({ at: 'tabs' }); setReloadKey((n) => n + 1) }}
          />
        )}
      </main>

      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}
      <InstallPrompt />

      <footer className="pagefoot">
        <span>Paradise Property Care · Vieques, Puerto Rico</span>
        <nav className="legal row">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/support">Support</a>
        </nav>
        {/*
          * The build, visible and unmissable, so "which one am I testing?" is
          * never a question. Every part is generated from the commit by
          * scripts/stamp-version.mjs; nothing here is typed by hand.
          */}
        <span className="right">
          v{VERSION} · Build {BUILD} · {COMMIT}{health ? ` · ${health.environment}` : ''}
          {' · '}
          <button type="button" className="link" onClick={refreshApp}>Refresh</button>
        </span>
      </footer>
    </ErrorBoundary>
  )
}
